import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, Info, X, Undo2 } from 'lucide-react';
import './Toast.css';

const ToastContext = createContext(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
}

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((message, options = {}) => {
    const id = ++toastId;
    const toast = {
      id,
      message,
      type: options.type || 'success',
      duration: options.duration || 4000,
      undoAction: options.undoAction || null,
      undoLabel: options.undoLabel || 'Undo',
    };
    setToasts(prev => [...prev, toast]);

    if (!options.undoAction) {
      setTimeout(() => removeToast(id), toast.duration);
    } else {
      // Undo toasts stay longer
      setTimeout(() => removeToast(id), options.undoDuration || 6000);
    }

    return id;
  }, [removeToast]);

  const toast = useCallback((message, options) => addToast(message, options), [addToast]);
  toast.success = (message, options) => addToast(message, { ...options, type: 'success' });
  toast.error = (message, options) => addToast(message, { ...options, type: 'error' });
  toast.warning = (message, options) => addToast(message, { ...options, type: 'warning' });
  toast.info = (message, options) => addToast(message, { ...options, type: 'info' });
  toast.undo = (message, undoAction, options) => addToast(message, { ...options, type: 'success', undoAction, undoDuration: 6000 });

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="toast-container">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={() => removeToast(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }) {
  const [exiting, setExiting] = useState(false);

  const handleDismiss = useCallback(() => {
    setExiting(true);
    setTimeout(onDismiss, 250);
  }, [onDismiss]);

  const handleUndo = useCallback(() => {
    if (toast.undoAction) toast.undoAction();
    handleDismiss();
  }, [toast, handleDismiss]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setExiting(true);
      setTimeout(onDismiss, 250);
    }, toast.undoAction ? 5750 : toast.duration - 250);
    return () => clearTimeout(timeout);
  }, [toast, onDismiss]);

  const icons = {
    success: <CheckCircle size={18} />,
    error: <AlertCircle size={18} />,
    warning: <AlertTriangle size={18} />,
    info: <Info size={18} />,
  };

  return (
    <div className={`toast-item toast-${toast.type} ${exiting ? 'toast-exit' : 'toast-enter'}`}>
      <span className="toast-icon">{icons[toast.type]}</span>
      <span className="toast-message">{toast.message}</span>
      {toast.undoAction && (
        <button className="toast-undo-btn" onClick={handleUndo}>
          <Undo2 size={14} /> {toast.undoLabel}
        </button>
      )}
      <button className="toast-close" onClick={handleDismiss}><X size={14} /></button>
    </div>
  );
}
